import type { Root } from "@aidkitorg/types/lib/survey";
import React, { useState, useEffect } from "react";
import { ChangeSet, groupObjectsFromDisjointedChanges, getWinningOperation, Move, State, TrackedObject, Author } from "@aidkitorg/typesheets/lib/collab";
import type { ChangesToRender } from "@aidkitorg/typesheets/lib/editor";
import { Button, CodeDisplay } from "@aidkitorg/component-library";
import { RenderDistro, findRenderableParent, ChangeBreadcrumb, isFullyAlive, getFieldPath } from "./ChangeUtils";

export type ChangesToRenderMap = Record<string, ChangesToRender>;

export function ChangeList({
  state,
  changes,
  parentState,
}: {
  state: State | undefined,
  changes: ChangesToRenderMap,
  parentState?: State,
}) {
  const [expandedDistros, setExpandedDistros] = useState<Record<string, boolean>>({});

  return (
    <ul role="list">
      {Object.keys(changes).map((id) => {
        const objId = id.startsWith('root-') ? id.split('-')[1] : id;
        const isRoot = id.startsWith('root-');
        const fieldPathParentId = isRoot ? 'root' : objId;

        return (
          <li className="p-2 m-2 bg-white rounded shadow" key={objId}>
            <div className="flex justify-between items-start px-1">
              <ChangeBreadcrumb
                obj={state?.workingSet[objId]}
                isRoot={isRoot}
                objId={objId}
                parentState={parentState}
              />
              <Button size="sm" variant="secondary" onClick={() => {
                setExpandedDistros({
                  ...expandedDistros,
                  [objId]: !expandedDistros[objId]
                });
              }}>{expandedDistros[objId] ? 'Collapse Distro' : 'Expand as Distro'}
              </Button>
            </div>
            <div className="mx-4 mt-2 space-y-2">
              {changes[id].additions.map((addition) => {
                const readObject = addition.object.read();
                const fieldPath = getFieldPath(addition.object, fieldPathParentId);
                return <div key={addition.object.id}>
                  {(addition.authors || fieldPath.length > 0) && <div className="text-xs mt-1 flex justify-between gap-3">
                    {fieldPath.length > 0 && <span className="font-mono text-gray-500">{fieldPath.join('.')}</span>}
                    {addition.authors && <span>{addition.authors.length > 1 ? 'Authors: ' : 'Author: '}{addition.authors.map(author => author.name).join(', ')}</span>}
                  </div>}
                  <CodeDisplay className="text-sm px-2 py-1 bg-green-100 text-black rounded whitespace-pre-wrap break-words block overflow-auto"
                    code={JSON.stringify(readObject ?? null, null, 2)}
                    language="json"
                    showLineNumbers={false}
                    copyable={false}
                    expandable={true}
                  />
                </div>
              })}
              {changes[id].removals.map((removal) => {
                const readObject = removal.object.read();
                // If the object's data was pruned from compressed ops, fall back to the parent ops state
                // which has the full content of all objects as they were at branch creation time.
                const isEmpty = typeof readObject !== 'object' || readObject === null
                  ? false
                  : Array.isArray(readObject)
                    ? readObject.length === 0
                    : Object.keys(readObject).length === 0;
                const parentObj = parentState?.workingSet[removal.object.id];
                const displayObject = isEmpty && parentObj ? parentObj.read() : readObject;
                const fieldPath = getFieldPath(removal.object, fieldPathParentId);
                return <div key={removal.object.id}>
                  <CodeDisplay className="text-sm px-2 py-1 bg-red-100 text-black rounded whitespace-pre-wrap break-words block overflow-auto"
                    code={JSON.stringify(displayObject ?? null, null, 2)}
                    language="json"
                    showLineNumbers={false}
                    copyable={false}
                    expandable={true}
                  />
                  {(removal.authors || fieldPath.length > 0) && <div className="text-xs mt-1 flex justify-end gap-3">
                    {fieldPath.length > 0 && <span className="font-mono text-gray-400">{fieldPath.join('.')}</span>}
                    {removal.authors && <span>{removal.authors.length > 1 ? 'Authors: ' : 'Author: '}{removal.authors.map(author => author.name).join(', ')}</span>}
                  </div>}
                </div>
              })}
            </div>

            {expandedDistros[objId] && <RenderDistro object={state?.workingSet[objId]} changes={changes[id]} parentIsRoot={id.startsWith('root-')} />}
          </li>
        )
      })}
    </ul>
  );
}

export function Changes(props: {
  stateRef: React.MutableRefObject<State | undefined>,
  collabEnabled: boolean,
  branchId: number | undefined,
  survey: Root | null,
  parentStateRef?: React.MutableRefObject<State | undefined>,
  lastPublished?: string | null,
}) {
  const { stateRef: localStateRef, collabEnabled, branchId, survey, parentStateRef, lastPublished } = props;
  const [changesToRender, setChangesToRender] = useState<ChangesToRenderMap>({});

  const [stateIsPrepared, setStateIsPrepared] = useState(false);

  // Reset when branch/collab changes. Hold off rendering until we have a branchId.
  useEffect(() => {
    setChangesToRender({});
    if (collabEnabled && !branchId) {
      setStateIsPrepared(false);
      return;
    }
    setStateIsPrepared(true);
  }, [collabEnabled, branchId]);

  // This useEffect does all the work of figuring out what to render.
  // It runs whenever the survey changes (user edits) or when stateIsPrepared changes.
  // It reads directly from localStateRef so it always reflects the latest editor state.
  useEffect(() => {
    if (!stateIsPrepared) return;

    const state = localStateRef.current;
    const { changes } = buildChangesToRender(state, state?.unpublishedChanges || [], parentStateRef?.current);
    setChangesToRender(changes);
  }, [survey, stateIsPrepared, lastPublished])

  return <div className="bg-gray-50 min-h-screen">
    <div className="text-lg font-bold">Current Unpublished Changes: {Object.keys(changesToRender).length === 0 ? 'None!' : ''}</div>
    <ChangeList
      key={`${collabEnabled}-${branchId}`}
      state={localStateRef.current}
      changes={changesToRender}
      parentState={parentStateRef?.current}
    />
  </div>
}

/**
 * @param events - The primary diff events (eventsAdded for PublishedChangesModal, unpublishedChanges for Changes tab).
 * @param removedEvents - Optional stale ops removed from published via compression (eventsRemoved).
 *   Passed separately so we can distinguish genuine new events from cleanup artifacts.
 */
export function buildChangesToRender(state: State | undefined, events: ChangeSet, parentState?: State, removedEvents?: ChangeSet): { changes: ChangesToRenderMap } {
  const allEvents = removedEvents ? [...events, ...removedEvents] : events;
  const groupedChanges = groupObjectsFromDisjointedChanges(allEvents);

  // eventOpIds tracks only the primary (added) events — used to distinguish genuine new
  // changes from stale ops that appear only in removedEvents.
  const eventOpIds = new Set(events.map(([, , op]) => op?.id).filter(Boolean));

  // Next, we figure out which of these grouped changes are "relevant".
  // Some changes, for example, are objects getting added and then immediately removed. This happens a lot -
  // when someone adds a Section, then realizes that they want a Subsurvey. That is a Section added and then deleted.
  // This change is not relevant, and we do not want to show it.
  const relevantChanges: Record<string, { object: TrackedObject, events: ChangeSet }> = {};
  for (const objId of Object.keys(groupedChanges)) {
    const objInSurvey = state?.workingSet[objId];

    // no parent or orphaned in a deleted subtree = removal.
    if (!isFullyAlive(objInSurvey)) {
      // Guard: when removedEvents are present, skip objects that only appear in allEvents
      // because of stale eventsRemoved ops — they represent old compressed-out history,
      // not genuine published changes.
      if (removedEvents && !groupedChanges[objId].events.some(e => e[2]?.id && eventOpIds.has(e[2].id))) {
        continue;
      }

      const unpublishedObjMoves = groupedChanges[objId].events
        .filter((event): event is [string, string, Move] => {
          if (event[2].op !== 'move') return false;
          // Exclude eventsRemoved ops — old history, not new published changes
          if (removedEvents && !(event[2]?.id && eventOpIds.has(event[2].id))) return false;
          return true;
        })
        .map(event => event[2]);

      const hasDeletionOp = unpublishedObjMoves.some(m => !m.loc);

      // Also treat as deleted if the winning move destination is no longer alive in state —
      // the object was moved into a container that was subsequently deleted.
      const winningMove = getWinningOperation(unpublishedObjMoves);
      const movedToDeletedContainer = !!winningMove?.loc?.id && !isFullyAlive(state?.workingSet[winningMove.loc.id]);

      let hasPreExistingLocation = false;
      if (hasDeletionOp || movedToDeletedContainer) {
        if (parentState) {
          hasPreExistingLocation = !!parentState.workingSet[objId];
        } else {
          // Fallback for old branches: winning loc not in unpublished set means it pre-existed.
          const winningLoc = getWinningOperation(state?.objects[objId]?.locations || []);
          hasPreExistingLocation = !!winningLoc?.loc && !unpublishedObjMoves.some(m => m.id === winningLoc.id);
        }
      }

      if (hasPreExistingLocation) {
        relevantChanges[objId] = {
          object: state?.workingSet[objId] as TrackedObject,
          events: groupedChanges[objId].events
        };
      }
    } else {
      // if it has a parent, it does still exist in the survey, so these changes are relevant.
      // Guard: when removedEvents are present, skip objects that only appeared in the diff because
      // their old placement was compressed out (eventsRemoved) — if none of the group's events
      // are from the primary eventsAdded set, this object wasn't actually changed.
      if (removedEvents) {
        const winningLoc = getWinningOperation(state?.objects[objId]?.locations || []);
        const hasNewLocation = !!winningLoc && eventOpIds.has(winningLoc.id);
        const hasNewEvent = groupedChanges[objId].events.some(e => e[2]?.id && eventOpIds.has(e[2].id));
        if (!hasNewLocation && !hasNewEvent) continue;
      }
      relevantChanges[objId] = {
        object: state?.workingSet[objId] as TrackedObject,
        events: groupedChanges[objId].events
      }
    }
  }

  // Surface pre-existing objects whose events were absorbed into a group leader because their
  // parent container was newly created (so it had no parent in the partial state used by
  // groupObjectsFromDisjointedChanges). Two sub-cases:
  //   1. Container is still alive: object was moved in → show as addition.
  //   2. Container was also deleted: object is effectively gone → show as deletion.
  // Group events by object id once up front — filtering `events` per object inside the
  // loop below is O(n²) and freezes the tab on large unpublished sets (a whole-program
  // paste is ~130k events).
  const eventsByObjectId = new Map<string, ChangeSet>();
  for (const event of events) {
    const list = eventsByObjectId.get(event[0]);
    if (list) {
      list.push(event);
    } else {
      eventsByObjectId.set(event[0], [event]);
    }
  }

  const seenMovedIds = new Set<string>();
  for (const [objId, , op] of events) {
    if ((op as Move).op !== 'move') continue;
    if (relevantChanges[objId] || seenMovedIds.has(objId)) continue;
    seenMovedIds.add(objId);

    const objInSurvey = state?.workingSet[objId];
    const objEvents = eventsByObjectId.get(objId) ?? [];
    // Use the winning move across all of this object's events — not just the first op
    // encountered in the loop, which may be an older/non-winning op (e.g. a prior deletion).
    const objMoveOps = objEvents
      .filter(([, , evOp]) => (evOp as Move).op === 'move')
      .map(([, , evOp]) => evOp as Move);
    const winningMove = getWinningOperation(objMoveOps);

    if (isFullyAlive(objInSurvey)) {
      // Object is fully alive (unbroken chain to root) — surface if pre-existed (moved, not newly created)
      if (!winningMove?.loc) continue;
      const preExisted = parentState
        ? !!parentState.workingSet[objId]
        : (state?.objects[objId]?.locations ?? []).some(loc => !eventOpIds.has(loc.id));
      if (preExisted) {
        relevantChanges[objId] = { object: objInSurvey!, events: objEvents };
      }
    } else {
      // Object is effectively gone (deleted or in an orphaned subtree) — check if it was moved
      // into a newly-created container that was then deleted.
      const winningPlacement = getWinningOperation(objMoveOps.filter(m => !!m.loc));
      if (winningPlacement?.loc?.id) {
        const containerId = winningPlacement.loc.id;
        // Container is "new and deleted" if it wasn't in parentState AND isn't fully alive now.
        // (All workingSet entries exist even for deleted objects, so we check aliveness not presence.)
        const containerNewAndDeleted = !parentState?.workingSet[containerId]
          && !isFullyAlive(state?.workingSet[containerId]);

        if (containerNewAndDeleted) {
          // Skip objects whose deletion is covered by a pre-existing ancestor that was
          // also deleted (e.g. a nested component inside a pre-existing collection A which
          // was moved into new-and-deleted B — A's deletion covers its descendants).
          let containerParent = state?.workingSet[containerId]?.parent;
          let coveredByAncestor = false;
          while (containerParent) {
            if (parentState?.workingSet[containerParent.id] && !isFullyAlive(containerParent)) {
              coveredByAncestor = true;
              break;
            }
            containerParent = containerParent.parent;
          }
          if (!coveredByAncestor) {
            const preExisted = parentState
              ? !!parentState.workingSet[objId]
              : (state?.objects[objId]?.locations ?? []).some(loc => !eventOpIds.has(loc.id));
            const parentObj = parentState?.workingSet[objId] ?? state?.workingSet[objId];
            if (preExisted && parentObj) {
              // Prefer parentState object so the removal display shows pre-existing content;
              // fall back to current state when parentState is unavailable (older branches).
              relevantChanges[objId] = { object: parentObj, events: objEvents };
            }
          }
        }
      }
    }
  }

  // Now we get our changes ready to render.
  const changesToRender: Record<string, ChangesToRender> = {};
  for (const objId of Object.keys(relevantChanges)) {
    // For live objects, use the current winning location.
    const activeLocs = state?.objects[objId]?.locations.filter(l => !!l.loc) ?? [];
    const lastLoc = getWinningOperation(activeLocs)?.loc;
    const uniqueAuthors: Record<string, Author> = {};
    for (const event of relevantChanges[objId].events) {
      const author = event[2].author;
      if (author) {
        uniqueAuthors[author.name + '/' + author.tabId] = author;
      }
    }
    const authors = Object.values(uniqueAuthors);

    const objIsFullyAlive = isFullyAlive(state?.workingSet[objId]);

    // Skip dead objects whose deletion is already covered by a dead ancestor also in
    // relevantChanges — the ancestor's card will show the full subtree including this object.
    if (!objIsFullyAlive) {
      let coveredByDeletingAncestor = false;
      let ancestor = parentState?.workingSet[objId]?.parent;
      while (ancestor) {
        if (relevantChanges[ancestor.id] && !isFullyAlive(state?.workingSet[ancestor.id])) {
          coveredByDeletingAncestor = true;
          break;
        }
        ancestor = ancestor.parent;
      }
      if (coveredByDeletingAncestor) continue;
    }

    let parentToRender = findRenderableParent(state?.workingSet[objId], parentState);
    // Discard a parentToRender that is itself in an orphaned subtree — fall through to the
    // "removed" path so we can resolve the correct display location via parentState.
    // Exception: when the object is its own renderable parent (e.g. a deleted Collection),
    // keep it so the object renders as its own card rather than being grouped under an ancestor.
    if (parentToRender && parentToRender.id !== objId && !isFullyAlive(parentToRender)) {
      parentToRender = undefined;
    }
    if (parentToRender) {
      const changeId = parentToRender.id === 'root' ? `root-${objId}` : parentToRender.id;
      // if the parent is root, group it separately from other root children. We don't want to render the whole root at once.
      changesToRender[changeId] ||= {
        additions: [],
        removals: []
      };

      if (objIsFullyAlive) {
        changesToRender[changeId].additions.push({
          object: relevantChanges[objId].object,
          location: lastLoc,
          authors: authors
        });
      } else {
        changesToRender[changeId].removals.push({
          object: relevantChanges[objId].object,
          location: lastLoc,
          authors: authors
        });
      }
      continue;
    }

    // If we haven't found how to place the thing, it means its been removed and we need to look at previous parents.
    // Prefer the latest placement from unpublished events (reflects in-branch moves) over the
    // parent ops snapshot so the deletion shows up in the right place after a move-then-delete.
    if (!objIsFullyAlive) {
      const unpublishedMoves = relevantChanges[objId].events
        .filter(e => {
          if (e[2].op !== 'move') return false;
          // Exclude eventsRemoved ops when computing last placement
          if (removedEvents && !(e[2]?.id && eventOpIds.has(e[2].id))) return false;
          return true;
        })
        .map(e => e[2] as Move);
      const latestPlacement = getWinningOperation(unpublishedMoves.filter(m => !!m.loc));

      // Prefer parentState for the container so the deletion shows under where the object
      // originally lived, not inside a newly-created container that was then deleted.
      // If the container isn't in parentState (it was new), fall back to the deleted object's
      // own parent in parentState, then finally to state (last resort).
      const lastParent = latestPlacement?.loc?.id
        ? (parentState?.workingSet[latestPlacement.loc.id]
            ?? parentState?.workingSet[objId]?.parent
            ?? state?.workingSet[latestPlacement.loc.id])
        : parentState?.workingSet[objId]?.parent;
      if (lastParent) {
        // the last parent might not be a renderable parent, so we need to find the nearest renderable parent.
        parentToRender = findRenderableParent(lastParent, parentState);
        if (parentToRender) {
          changesToRender[parentToRender.id === 'root' ? `root-${objId}` : parentToRender.id] ||= {
            additions: [],
            removals: []
          };
          changesToRender[parentToRender.id === 'root' ? `root-${objId}` : parentToRender.id].removals.push({
            object: relevantChanges[objId].object,
            location: lastLoc,
            authors: authors
          });
          continue;
        }
      }
    }

    // This means we do not know how to render the object.
    // We would love to get to a place where nothing falls in this category.
    changesToRender[objId] ||= {
      additions: [],
      removals: []
    };

    if (objIsFullyAlive) {
      changesToRender[objId].additions.push({
        object: relevantChanges[objId].object,
        location: lastLoc,
        authors: authors
      });
    } else {
      changesToRender[objId].removals.push({
        object: relevantChanges[objId].object,
        location: lastLoc,
        authors: authors
      });
    }
  }

  return { changes: changesToRender };
}
